<template>
  <div class="auth-form-container">
    <h2>Vaihda salasana</h2>
    <p class="instructions">
      Syötä sähköpostiisi lähetetty vahvistuskoodi ja uusi salasana.
    </p>
    <form @submit.prevent="resetPassword">
      <input 
        v-model="username" 
        placeholder="Käyttäjänimi tai sähköposti" 
        required 
      />
      <input 
        v-model="code" 
        placeholder="Vahvistuskoodi" 
        required 
      />
      <input 
        v-model="newPassword" 
        placeholder="Uusi salasana" 
        type="password" 
        required 
      />
      <button type="submit" :disabled="isLoading">
        {{ isLoading ? 'Vaihdetaan...' : 'Vaihda salasana' }}
      </button>
    </form>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div v-if="success" class="success-message">{{ success }}</div>
    <div class="auth-link">
      <router-link to="/sign-in">Takaisin kirjautumiseen</router-link>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Auth } from 'aws-amplify';

export default {
  name: 'ResetPassword',
  setup() {
    const username = ref('');
    const code = ref('');
    const newPassword = ref('');
    const error = ref('');
    const success = ref('');
    const isLoading = ref(false);
    const router = useRouter();
    const route = useRoute();

    onMounted(() => {
      // Get username from query params if available
      if (route.query.username) {
        username.value = route.query.username;
      }
    });

    const resetPassword = async () => {
      error.value = '';
      success.value = '';
      isLoading.value = true;
      
      try {
        await Auth.forgotPasswordSubmit(
          username.value,
          code.value,
          newPassword.value
        );
        
        success.value = 'Salasana vaihdettu onnistuneesti!';
        
        // Redirect to sign in page after a short delay
        setTimeout(() => {
          router.push('/sign-in');
        }, 2000);
      } catch (err) {
        if (err.code === 'CodeMismatchException') {
          error.value = 'Virheellinen vahvistuskoodi. Tarkista koodi ja yritä uudelleen.';
        } else if (err.code === 'ExpiredCodeException') {
          error.value = 'Vahvistuskoodi on vanhentunut. Pyydä uusi koodi.';
        } else if (err.code === 'InvalidPasswordException') {
          error.value = 'Salasana ei täytä vaatimuksia. Salasanan tulee olla vähintään 8 merkkiä pitkä ja sisältää isoja ja pieniä kirjaimia sekä numeroita.';
        } else {
          error.value = err.message || 'Salasanan vaihtamisen aikana tapahtui virhe.';
        }
        console.error('Reset password error:', err);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      username,
      code,
      newPassword,
      error,
      success,
      isLoading,
      resetPassword,
    };
  },
};
</script>

<style scoped>
/* Reuse the same styles as other auth components */
.auth-form-container {
  max-width: 450px;
  margin: 30px auto;
  padding: 30px 40px;
  background-color: #112240;
  border-radius: 8px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
}

.auth-form-container h2 {
  color: #ffffff;
  text-align: center;
  margin-bottom: 15px;
  font-size: 1.8rem;
  font-weight: 500;
}

.instructions {
  color: #a8b2d1;
  text-align: center;
  margin-bottom: 20px;
  font-size: 0.95rem;
  line-height: 1.5;
}

.auth-form-container form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.auth-form-container input[type="text"],
.auth-form-container input[type="email"],
.auth-form-container input[type="password"],
.auth-form-container input:not([type]) {
  background-color: #0a192f;
  border: 1px solid #1e3a5f;
  border-radius: 4px;
  color: #e6f1ff;
  padding: 12px 15px;
  font-size: 1rem;
  width: 100%;
  transition: all 0.3s ease;
}

.auth-form-container input:focus {
  outline: none;
  border-color: #64ffda;
  box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.2);
}

.auth-form-container button {
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  border-radius: 4px;
  padding: 12px 15px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.auth-form-container button:hover:not(:disabled) {
  background-color: #4cd8b9;
}

.auth-form-container button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-message {
  color: #ff6b6b;
  margin-top: 15px;
  text-align: center;
  font-size: 0.9rem;
}

.success-message {
  color: #64ffda;
  margin-top: 15px;
  text-align: center;
  font-size: 0.9rem;
}

.auth-link {
  margin-top: 20px;
  text-align: center;
  font-size: 0.9rem;
}

.auth-link a {
  color: #64ffda;
  text-decoration: none;
  transition: color 0.3s ease;
}

.auth-link a:hover {
  color: #4cd8b9;
  text-decoration: underline;
}
</style>